import { type ReactNode, useCallback, useMemo } from 'react';
import { useReportAccessMatrix, useUpdateReportAccess } from '@features/reports';
import { useSubjectLanguageVariants } from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { practicePermissionCopy } from '../utils/practicePermissionCopy';

import { SectionCardTitle } from './SectionCardTitle';

interface ReportAccessMatrixProps {
  /** Guide control, inline after the card title (GDN-3703). */
  titleControl?: ReactNode;
  onClose?: () => void;
}

type ReportAccessScope = 'all' | 'own';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === 'string') {
      return data.message;
    }
  }

  return 'Failed to update report access';
}

export function ReportAccessMatrix({ titleControl, onClose }: ReportAccessMatrixProps = {}) {
  const subject = useSubjectLanguageVariants();
  const copy = (text: string) => practicePermissionCopy(text, subject);
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useReportAccessMatrix();
  const [updateReportAccess, { isLoading: isUpdating }] = useUpdateReportAccess();

  const reports = useMemo(() => data?.reports ?? [], [data?.reports]);
  const headerRoles = reports[0]?.roles ?? [];

  const handleToggle = useCallback(
    async (reportKey: string, role: string, enabled: boolean) => {
      try {
        await updateReportAccess({
          updateReportAccessRequest: {
            report_key: reportKey,
            role,
            enabled,
          },
        }).unwrap();
        enqueueSnackbar('Report access updated', { variant: 'success' });
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [enqueueSnackbar, updateReportAccess],
  );

  // Scope changes always accompany an enabled cell — the server rejects
  // 'own' on a non-scopable report or a MANAGE_REPORTS-implied role with a
  // 400 (AC-4); on failure the query cache is untouched, so the cell simply
  // keeps showing its previous stored value.
  const handleScopeChange = useCallback(
    async (reportKey: string, role: string, scope: ReportAccessScope) => {
      try {
        await updateReportAccess({
          updateReportAccessRequest: {
            report_key: reportKey,
            role,
            enabled: true,
            scope,
          },
        }).unwrap();
        enqueueSnackbar('Report access updated', { variant: 'success' });
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [enqueueSnackbar, updateReportAccess],
  );

  if (isLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading report access...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<TuneIcon color="primary" />}
        title={<SectionCardTitle title="Report access" control={titleControl} />}
        slotProps={{ title: { variant: 'h6', fontWeight: 600 } }}
        subheader="Choose which roles can view and run each report."
        action={
          onClose && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Report access could not be loaded.
          </Alert>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              {headerRoles.map(role => (
                <col key={role.role} style={{ width: `${74 / (headerRoles.length || 1)}%` }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                {headerRoles.map(role => (
                  <TableCell key={role.role} align="center" sx={{ fontWeight: 600 }}>
                    {role.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map(report => (
                <TableRow key={report.report_key}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {copy(report.title)}
                    </Typography>
                    {report.description && (
                      <Typography variant="caption" color="text.secondary">
                        {copy(report.description)}
                      </Typography>
                    )}
                  </TableCell>
                  {report.roles.map(cell => {
                    const accessSwitch = (
                      <Switch
                        checked={cell.implied_by_manage_reports || cell.enabled}
                        disabled={cell.implied_by_manage_reports || isUpdating}
                        onChange={event =>
                          handleToggle(report.report_key, cell.role, event.target.checked)
                        }
                        slotProps={{
                          input: {
                            'aria-label': `Toggle ${copy(report.title)} for ${cell.label}`,
                          },
                        }}
                      />
                    );

                    // GDN-3292 (Slice A, AC-1/AC-3): scopable row + enabled,
                    // non-implied cell → live scope control. Implied cells on
                    // a scopable row show a static "All rows" label instead
                    // (locked, no control). Non-scopable rows and disabled
                    // cells never show either — unchanged two-state cell.
                    // Ships ungated at Jackson's direction: the control is
                    // inert until an administrator stores an "Own rows"
                    // scope, so it was guarding a control rather than data.
                    const showScopeControl =
                      report.scopable && cell.enabled && !cell.implied_by_manage_reports;
                    const showLockedScopeLabel = report.scopable && cell.implied_by_manage_reports;

                    return (
                      <TableCell key={cell.role} align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            minHeight: 36,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {cell.overridden && (
                              <Tooltip title="Modified from this report's default.">
                                <Box
                                  component="span"
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                  }}
                                />
                              </Tooltip>
                            )}
                            {cell.implied_by_manage_reports ? (
                              <Tooltip title="Report managers always have access.">
                                <span>{accessSwitch}</span>
                              </Tooltip>
                            ) : (
                              accessSwitch
                            )}
                          </Box>

                          {showLockedScopeLabel && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}
                            >
                              All rows
                            </Typography>
                          )}

                          {/* GDN-3305: dropdown, not the SegmentedControl the
                              cell shipped with — picked from prototype round
                              report-access-dropdown-2026-09's "scope dropdown
                              only" concept. Presentation only: same
                              showScopeControl condition, same
                              handleScopeChange mutation call below. */}
                          {showScopeControl && (
                            <Select
                              size="small"
                              value={cell.scope ?? 'all'}
                              disabled={isUpdating}
                              onChange={event =>
                                handleScopeChange(report.report_key, cell.role, event.target.value)
                              }
                              inputProps={{
                                'aria-label': `${copy(report.title)} scope for ${cell.label}`,
                              }}
                              sx={{
                                fontSize: '0.8125rem',
                                '& .MuiSelect-select': { py: 0.25, px: 1 },
                              }}
                            >
                              <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>
                                All rows
                              </MenuItem>
                              <MenuItem value="own" sx={{ fontSize: '0.8125rem' }}>
                                Own rows
                              </MenuItem>
                            </Select>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isUpdating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Updating report access...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
